import { type AllowanceDetail } from "@kanpla/api-contract";

import { priceFormatter } from "~/utils/price-formatter";
import { type PosProduct } from "~/types/product";
import { type Currency, type Discount } from "~/api/products/types";
import { getHybridBillingProductDiscount } from "~/utils/getHybridBillingProductDiscount";
import { shouldShowListStrikethrough } from "~/utils/hybrid-billing-display";

import { type ProductBasicWithTaxRate } from "../../types";
import { type ProductBasket } from "../../store.types";

type ParseInProductsPosArgs = {
  products: ProductBasicWithTaxRate[];
  discounts: Discount[];
  allowances: AllowanceDetail[];
  locale: string | null;
  currency: Currency;
  basket: ProductBasket[];
  showPricesInclVat: boolean;
};

export function parseInProductsPos({
  products,
  discounts,
  allowances,
  locale,
  currency,
  basket,
  showPricesInclVat
}: ParseInProductsPosArgs): PosProduct[] {
  const productsPos: PosProduct[] = [];

  for (const product of products) {
    const basketProduct = basket.find((item) => item.productId === product.id);

    const quantity = basketProduct?.count || 0;

    const hybridBillingDiscount = getHybridBillingProductDiscount({
      product: {
        discount: product.discount ?? null,
        unitPrice: product.unitPrice
      },
      discounts,
      allowances
    });

    const shouldDisplayDiscountedPrice = shouldShowListStrikethrough(
      hybridBillingDiscount
    );

    const basePrice = showPricesInclVat
      ? product.unitPrice
      : product.exclVatUnitPrice;

    // Takes into account discounts applied to the product
    const displayPrice = priceFormatter({
      value:
        basePrice -
        (shouldDisplayDiscountedPrice ? hybridBillingDiscount?.amount || 0 : 0),
      currency,
      locale
    });

    // Original product price, if there are no discounts will be the same as displayPrice
    const displayPriceOriginal = priceFormatter({
      value: basePrice,
      currency,
      locale
    });

    const totalDisplayPrice = priceFormatter({
      value: basePrice * quantity,
      currency,
      locale
    });

    productsPos.push({
      ...product,
      displayPrice,
      displayPriceOriginal,
      totalDisplayPrice,
      quantity,
      discount: hybridBillingDiscount
    });
  }

  return productsPos;
}
